/**
 * context/SessionContext
 * ──────────────────────────────────────────────────────────────────────────
 * Who is signed in, and what happens when they stop being signed in.
 *
 * The whole of this product's auth handling is here and in
 * services/session — there is no login screen, because sign-in belongs to
 * skplatform-fe. Three states:
 *
 *   'loading'  the probe is in flight; the shell shows nothing yet
 *   'signed-in' we have a user and an org
 *   'signed-out' the probe returned 401 — on web the app redirects to the
 *               platform sign-in with a return_to; on native the platform app
 *               owns the hand-off, so the app shows the sign-in prompt
 *
 * It also installs the axios session-expired handler, so a refresh failure
 * mid-session lands in exactly the same place as a failed boot probe rather
 * than leaving screens to discover 401s one at a time.
 * ──────────────────────────────────────────────────────────────────────────
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { setSessionExpiredHandler } from '../api/axiosInstance';
import { loadTokens } from '../storage/tokenStore';
import {
  fetchSession,
  invalidateSession,
  redirectToSignIn,
  signOut as signOutService,
} from '../services/session/session.service';
import { displayName, orgLine, type SessionOrg, type SessionUser } from '../schemas/session';
import { startAuthHandoffListener } from '../navigation/linking';

export type SessionStatus = 'loading' | 'signed-in' | 'signed-out';

export interface SessionContextValue {
  status: SessionStatus;
  user: SessionUser | null;
  org: SessionOrg | null;
  /** "Nikhil S." — for the header and for naming a decision. */
  userName: string;
  /** "Softknack Technologies · org_sk_001" — the header's org line. */
  orgLabel: string;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [org, setOrg] = useState<SessionOrg | null>(null);

  const refresh = useCallback(async () => {
    try {
      const payload = await fetchSession();
      setUser(payload.user ?? null);
      setOrg(payload.org ?? null);
      setStatus(payload.user ? 'signed-in' : 'signed-out');
    } catch {
      // A 401 here is the expected answer for a visitor with no session, not
      // a failure to report. Nothing is toasted.
      setUser(null);
      setOrg(null);
      setStatus('signed-out');
    }
  }, []);

  const signOut = useCallback(async () => {
    await signOutService();
    setUser(null);
    setOrg(null);
    setStatus('signed-out');
    redirectToSignIn();
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Native must read the stored token BEFORE the first request, or the probe
    // goes out unauthenticated and reports a false sign-out.
    (async () => {
      await loadTokens();
      if (!cancelled) await refresh();
    })();

    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    // Native only: the platform app returns the user here on a deep link
    // carrying their token. A no-op on web, where the cookie is already set.
    return startAuthHandoffListener(() => void refresh());
  }, [refresh]);

  useEffect(() => {
    // One place to land when a silent refresh fails, wherever it happened.
    setSessionExpiredHandler(() => {
      invalidateSession();
      setUser(null);
      setOrg(null);
      setStatus('signed-out');
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      user,
      org,
      userName: displayName(user),
      orgLabel: orgLine(org),
      refresh,
      signOut,
    }),
    [status, user, org, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}
